// Reconfigurable tiered flow-of-funds Sankey (Trello FkCeGYdu — "Sankey Refresh").
//
// The chart is assembled client-side from enriched transactions. Each
// granularity gets its own lateral column; the Account is the fixed center
// pivot, inflow tiers fan in from the left and outflow tiers fan out to the
// right. Which tiers show is driven by `enabledTiers`; `separateAccounts`
// toggles one-node-per-account vs. a single "All Accounts" node. Internal
// transfers are always excluded (a dedicated Chord view is a later card).
import SankeyChart from './sankey-chart-component.js';
import { forwardRef, useMemo, useState, useEffect } from 'react';
import { buildTieredSankeyData, tieredSankeyLayout } from './sankey-utils.js';
import { formatCurrency } from '../../utils/formatters.js';
import { Skeleton, Box } from '@mui/material';
import styles from './report-sankey-analysis.module.css';

// Quick-perspective presets. Each maps to a set of tier keys (Account is
// implicit and always shown). Exported because page-sankey-analysis.js reads
// it to validate the URL `:perspective` param and TransactionAnalyzer seeds
// the tier checkboxes from it. Keys kept from the legacy perspectives so
// existing nav links / bookmarks (/report_sankey_analysis/CATEGORY) still land.
export const perspectiveMap = {
  CATEGORY: {
    label: 'Category',
    description: 'Inflow & outflow by category',
    tiers: ['IC', 'OC'],
  },
  MERCHANT: {
    label: 'Merchant',
    description: 'Inflow & outflow by merchant',
    tiers: ['IM', 'OM'],
  },
  CATEGORY_TO_MERCHANT: {
    label: 'Category → Merchant',
    description: 'Category in; category → subcategory → merchant out',
    tiers: ['IC', 'OC', 'OS', 'OM'],
  },
  FULL: {
    label: 'Full Flow',
    description: 'Every tier — merchant to merchant',
    tiers: ['IM', 'IS', 'IC', 'OC', 'OS', 'OM'],
  },
};

export const DEFAULT_PERSPECTIVE = 'CATEGORY';

// Tier keys for a preset, or the CATEGORY default when the key is unknown.
export const tiersForPerspective = (perspective) =>
  perspectiveMap[perspective]?.tiers || perspectiveMap[DEFAULT_PERSPECTIVE].tiers;

const ReportCategorySankeyAnalysis = forwardRef((props, ref) => {
  const enrichedTransactions = useMemo(() => props.data || [], [props.data]);
  const isTransactionsLoading = props.isLoading || false;
  const parentHandleSelectData = useMemo(() => props.onSelectData || (() => { }), [props.onSelectData]);

  // Tier configuration. `enabledTiers` is the set of inflow/outflow tier keys
  // to show (Account is always present); `separateAccounts` false collapses
  // all accounts into one node. Defaults keep the component usable when a
  // caller renders it without the controls (e.g. tests).
  const enabledTiers = useMemo(
    () => {
      if (props.enabledTiers instanceof Set) return props.enabledTiers;
      if (props.enabledTiers) return new Set(props.enabledTiers);
      // Fall back to a preset when a caller passes the human-friendly
      // `perspective` prop instead of explicit `enabledTiers` (the tab
      // switchers speak in perspectives). Without this a perspective-only
      // caller is silently pinned to the default perspective no matter which
      // tab is selected. tiersForPerspective handles an unknown/undefined
      // value by returning the default tiers.
      return new Set(tiersForPerspective(props.perspective));
    },
    [props.enabledTiers, props.perspective],
  );
  const separateAccounts = props.separateAccounts !== false;

  const formatNodeValue = (node) => formatCurrency(node.value);
  const formatNodeLabel = (node) => node.name;
  const formatLinkLabel = (link) => `$${formatCurrency(link.value)}`;

  useEffect(() => {
    parentHandleSelectData(enrichedTransactions || []);
  }, [enrichedTransactions, parentHandleSelectData]);

  const handleSelectData = (transactionIDs) => {
    let selectedData = enrichedTransactions || [];
    if (transactionIDs && transactionIDs.size > 0) {
      selectedData = enrichedTransactions.filter(txn => transactionIDs.has(txn.transaction.id));
    }
    parentHandleSelectData(selectedData);
  };

  const handleLinkClick = (details) => handleSelectData(details.transactionIDs);
  const handleNodeClick = (details) => handleSelectData(details.transactionIDs);
  const handleBackgroundClick = () => handleSelectData(new Set());

  // Track viewport width so the Sankey re-renders with correct logical
  // dimensions on resize/zoom/case-switch. Declared above the loading
  // early-return so hook order stays stable.
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let frame = null;
    const handleResize = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setViewportWidth(window.innerWidth);
        frame = null;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  // Memoized transform — only re-runs when the inputs that change the output
  // change, so sibling re-renders (filter chips, table sort) skip the work.
  // MUST sit above the loading early-return to keep hook order stable.
  const sankeyData = useMemo(
    () => buildTieredSankeyData(enrichedTransactions || [], { enabledTiers, separateAccounts }),
    [enrichedTransactions, enabledTiers, separateAccounts],
  );

  if (isTransactionsLoading || !enrichedTransactions) {
    return (
      <Box sx={{ width: '100%', px: 4 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 3, borderRadius: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 1400 }}>
            <Skeleton variant="rectangular" height={800} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
      </Box>
    );
  }

  const anchorLevelNames = sankeyData.anchorLevelNames || [];
  const topN = props.displayOptions?.maxNodesPerType ?? 10; // top N per column (0 = all)

  const sankeyWidth = Math.max(800, Math.min(viewportWidth * 0.9, 1800));
  const sankeyHeight = Math.min(sankeyWidth * 0.6, 800);

  return (
    <>
      {sankeyData && sankeyData.nodes && sankeyData.links && sankeyData.nodes.length > 0 && sankeyData.links.length > 0 && (
        <Box sx={{ width: '100%', px: 'var(--space-4)' }}>
            <div className={styles.legend}>
              <span className={styles.item}>
                <span className={`${styles.swatch} ${styles.inflow}`} aria-hidden="true" />Inflows
              </span>
              <span className={styles.item}>
                <span className={`${styles.swatch} ${styles.account}`} aria-hidden="true" />Accounts
              </span>
              <span className={styles.item}>
                <span className={`${styles.swatch} ${styles.outflow}`} aria-hidden="true" />Outflows
              </span>
            </div>
            <SankeyChart
              ref={ref}
              data={sankeyData}
              anchorLevelNames={anchorLevelNames}
              topN={topN}
              customSankeyLayout={tieredSankeyLayout}
              showLinkLabels={false}
              highlightCycles={false}
              width={sankeyWidth}
              height={sankeyHeight}
              margin={{ top: 40, right: 200, bottom: 40, left: 200 }}
              wrapLabels={true}
              hideCrowdedLabels={true}
              formatNodeValue={formatNodeValue}
              formatNodeLabel={formatNodeLabel}
              formatLinkLabel={formatLinkLabel}
              nodePadding={28}
              nodeWidth={22}
              labelPadding={12}
              colorPalette={[
                "#1f77b4", "#2ca02c", "#9467bd", "#8c564b",
                "#e377c2", "#7f7f7f", "#bcbd22", "#17becf", "#d62728"
              ]}
              onLinkClick={handleLinkClick}
              onNodeClick={handleNodeClick}
              onBackgroundClick={handleBackgroundClick}
            />
        </Box>
      )}
    </>
  );
});

ReportCategorySankeyAnalysis.displayName = 'ReportCategorySankeyAnalysis';

export default ReportCategorySankeyAnalysis;
